import {
  AfterViewInit,
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  Injector,
  NgZone,
  OnDestroy,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { distinctUntilChanged, map } from "rxjs";

import {
  SymbolTable,
  SymbolCardView,
} from "@bitwarden/web-vault/app/components/tables/symbol-table/symbol.table";
import { TablesFactory } from "@bitwarden/web-vault/app/components/tables/tables.factory";
import { TablesType } from "@bitwarden/web-vault/app/models/enum/tables.enum";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import {
  defaultGroupForCode,
  type ISymbolView,
} from "@bitwarden/web-vault/app/models/view/symbol/symbol.view";
import { ReferenceDataSource } from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import { isCurrency } from "@bitwarden/web-vault/app/models/view/symbol/currency-utility";
import { MacroNumbers } from "@bitwarden/web-vault/app/models/enum/macroNumber.enum";
import { isGlossCreditsSymbol } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import {
  SymbolGroup,
  SymbolGroupIcon,
  SymbolGroupI18nKey,
  SymbolGroupOrder,
} from "@bitwarden/web-vault/app/models/types/symbol-group.types";
import { deepLinkParamReady$ } from "@bitwarden/web-vault/app/shared/utils/deep-link-selection.util";

import { holdingValue } from "./symbol-list-item/symbol-list-item.utils";

export function symbolGroupOf(item: SymbolCardView): SymbolGroup {
  return item.rawSymbol?.group ?? defaultGroupForCode(item.code);
}

// The rate-equation area's own layout: a single-line flex row by default, switching to a
// 2-column grid (native/core stacked in column 1, chip spanning both rows in column 2) once
// ManageSymbolsComponent's ResizeObserver-based check finds the row's content doesn't fit.
// Kept as plain Tailwind utility arrays (rather than a custom CSS class) so there's nothing to
// maintain outside the template. evaluateRateEquationOverflow() temporarily applies the flex
// classes to get a stable single-line-mode measurement (see there for why).
// tw-whitespace-nowrap: hyphenated codes (BTC-USD) would otherwise wrap at the hyphen, so the
// content never overflows and compact mode is never triggered.
const RATE_EQUATION_FLEX_CLASSES = [
  "tw-flex",
  "tw-min-w-0",
  "tw-gap-2",
  "tw-overflow-hidden",
  "tw-whitespace-nowrap",
];
const RATE_EQUATION_GRID_CLASSES = [
  "tw-grid",
  "tw-min-w-0",
  "tw-grid-cols-[minmax(0,auto)_auto]",
  "tw-grid-rows-2",
  "tw-justify-end",
  "tw-gap-x-2",
  "tw-gap-y-0.5",
  // Ellipsis truncation lives here (not on the elements) so it only exists in compact mode and is
  // dropped by evaluateRateEquationOverflow's measurement swap: overflow:hidden on these in
  // single-line mode makes Chrome under-size the area's max-content and truncates needlessly.
  "[&>[data-rate-col]]:tw-w-full",
  "[&>[data-rate-col]]:tw-min-w-0",
  "[&>[data-rate-col]]:tw-justify-end",
  "[&>[data-rate-col]]:tw-overflow-hidden",
  "[&_[data-rate-num]]:tw-min-w-0",
  "[&_[data-rate-num]]:tw-truncate",
  "[&_[data-rate-col]>span:last-child]:tw-shrink-0",
];

// Must match the default (non-compact) min-w-[96px] on the code column in the template.
const RATE_EQUATION_CODE_MIN_WIDTH = 96;

export interface SymbolGroupTotal {
  group: SymbolGroup;
  icon: string;
  labelKey: string;
  count: number;
  value: number;
}

// Pure and exported so the value-calculation edge cases (zero vs. never-tracked quantity,
// negative positions) can be unit tested without any Angular/DI setup - see
// symbol-group-total.util.spec.ts.
export function computeGroupTotals(
  items: SymbolCardView[],
  qtyByCode: Map<string, number>,
): SymbolGroupTotal[] {
  const totals = new Map<SymbolGroup, { count: number; value: number }>();
  for (const item of items ?? []) {
    const group = symbolGroupOf(item);
    const value = holdingValue(item.code, item.rawSymbol?.rate, qtyByCode);
    const entry = totals.get(group) ?? { count: 0, value: 0 };
    entry.count += 1;
    entry.value += value;
    totals.set(group, entry);
  }
  return SymbolGroupOrder.filter((group) => totals.has(group)).map((group) => ({
    group,
    icon: SymbolGroupIcon[group],
    labelKey: SymbolGroupI18nKey[group],
    ...totals.get(group),
  }));
}

@Component({
  selector: "app-symbol",
  templateUrl: "manage-symbols.component.html",
})
export class ManageSymbolsComponent implements AfterViewInit, OnDestroy {
  private dialogFactory: DialogFactory;
  private route = inject(ActivatedRoute);
  private transactionStore = inject(TransactionStoreService);
  table: SymbolTable = new TablesFactory().get(TablesType.Symbol);
  selectedSymbol = this.table.selectedSymbol;

  // Route params are available immediately, but on a cold load (bookmark, refresh, pasted link)
  // the symbol store hasn't populated yet. combineLatest with the table's data (rather than a
  // one-shot queryParams subscription) covers both timings (#4519 follow-up).
  private readonly symbolCode$ = this.route.queryParams.pipe(
    map((params) => params["symbolCode"] as string | undefined),
    distinctUntilChanged(),
  );
  protected isCurrency = isCurrency;

  // Added search signal to resolve the TS2339 compilation error
  searchQuery = signal<string>("");

  // Computed filter to dynamically filter rows by search value
  filteredFlatData = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const records = this.table.flatData() ?? [];
    if (!query) {
      return records;
    }
    return records.filter(
      (item) =>
        item.name?.toLowerCase().includes(query) ||
        item.code?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query),
    );
  });

  // Grouped view of the (search-filtered) list for the group-header rows in the list panel.
  groupedFilteredData = computed(() => this.groupByFixedOrder(this.filteredFlatData()));

  // Quantity held per symbol code, summed across every account's transaction-derived balance.
  private heldQuantityByCode = computed(() => {
    const byCode = new Map<string, number>();
    const balances = this.transactionStore.balanceByAccountSymbols.balances();
    if (!balances) {
      return byCode;
    }
    for (const bySymbol of balances.values()) {
      for (const [code, qty] of bySymbol) {
        byCode.set(code, (byCode.get(code) ?? 0) + qty);
      }
    }
    return byCode;
  });

  // Summary-strip data: per group, symbol count + total held value, always over the full
  // (unfiltered) list so the strip doesn't change while searching.
  groupTotals = computed(() =>
    computeGroupTotals(this.table.flatData() ?? [], this.heldQuantityByCode()),
  );

  private groupByFixedOrder(
    items: SymbolCardView[],
  ): { group: SymbolGroup; labelKey: string; items: SymbolCardView[] }[] {
    const byGroup = new Map<SymbolGroup, SymbolCardView[]>();
    for (const item of items ?? []) {
      const group = symbolGroupOf(item);
      if (!byGroup.has(group)) {
        byGroup.set(group, []);
      }
      byGroup.get(group).push(item);
    }
    return SymbolGroupOrder.filter((group) => byGroup.has(group)).map((group) => ({
      group,
      labelKey: SymbolGroupI18nKey[group],
      items: byGroup.get(group),
    }));
  }

  @ViewChild("groupStatsScroll") private groupStatsScrollRef?: ElementRef<HTMLElement>;
  protected canScrollGroupStatsLeft = signal(false);
  protected canScrollGroupStatsRight = signal(false);

  protected updateGroupStatsScrollArrows(): void {
    const el = this.groupStatsScrollRef?.nativeElement;
    if (!el) {
      return;
    }
    this.canScrollGroupStatsLeft.set(el.scrollLeft > 0);
    this.canScrollGroupStatsRight.set(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }

  protected scrollGroupStats(direction: 1 | -1): void {
    this.groupStatsScrollRef?.nativeElement.scrollBy({ left: direction * 160, behavior: "smooth" });
  }

  @HostListener("window:resize")
  onGroupStatsResize(): void {
    this.updateGroupStatsScrollArrows();
  }

  // The scroll container's width can change without a window resize (e.g. sidebar
  // collapse/expand), which the window:resize listener above can't catch.
  private groupStatsResizeObserver?: ResizeObserver;

  // IDs of rows whose rate-equation area (1.00 CODE / @1:rate chip / rate QUOTE) needs the
  // compact two-column layout. Driven by measured overflow (evaluateRateEquationOverflow), not
  // a fixed breakpoint - a breakpoint would apply the same threshold to every row regardless of
  // how long that particular row's own content is.
  protected compactRateEquationIds = signal<ReadonlySet<string>>(new Set());
  protected readonly rateEquationDefaultClasses = RATE_EQUATION_FLEX_CLASSES.join(" ");
  protected readonly rateEquationCompactClasses = RATE_EQUATION_GRID_CLASSES.join(" ");
  @ViewChildren("rateEquationArea") private rateEquationAreas!: QueryList<ElementRef<HTMLElement>>;
  // The rate-equation area itself stops tracking the row's width once compact (flex-shrink: 0,
  // so its own grid content doesn't get squeezed below what it needs) - so it can't also be
  // the signal for "there's room again, revert to single-line". #rowContent (code + rate-
  // equation area's shared parent) keeps tracking the row's actual available width regardless
  // of which mode its child is in, so it's what triggers re-evaluation on a widen-back.
  @ViewChildren("rowContent") private rowContents!: QueryList<ElementRef<HTMLElement>>;
  private rateEquationResizeObserver?: ResizeObserver;
  private observedRateEquationEls = new Set<HTMLElement>();
  private destroyed = false;
  private evaluationFrame?: number;
  private readonly onFontsLoaded = () => this.scheduleRateEquationEvaluation();
  private destroyRef = inject(DestroyRef);
  private ngZone = inject(NgZone);

  ngAfterViewInit(): void {
    this.updateGroupStatsScrollArrows();

    const el = this.groupStatsScrollRef?.nativeElement;
    if (el) {
      this.groupStatsResizeObserver = new ResizeObserver(() => this.updateGroupStatsScrollArrows());
      this.groupStatsResizeObserver.observe(el);
    }

    this.rateEquationResizeObserver = new ResizeObserver(() =>
      this.scheduleRateEquationEvaluation(),
    );
    // Web font swaps change content width without resizing the observed boxes.
    void document.fonts?.ready.then(() => this.scheduleRateEquationEvaluation());
    // Fonts loaded lazily after first paint (e.g. a later-used weight) fire loadingdone too.
    document.fonts?.addEventListener?.("loadingdone", this.onFontsLoaded);
    this.observeRateEquationAreas();
    this.rateEquationAreas.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.observeRateEquationAreas());
    this.rowContents.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.observeRateEquationAreas());
  }

  ngOnDestroy(): void {
    this.groupStatsResizeObserver?.disconnect();
    this.rateEquationResizeObserver?.disconnect();
    document.fonts?.removeEventListener?.("loadingdone", this.onFontsLoaded);
    this.destroyed = true;
    if (this.evaluationFrame !== undefined) {
      cancelAnimationFrame(this.evaluationFrame);
    }
  }

  // Only diffs the observed set (observe new, unobserve removed). No synchronous evaluate here:
  // ResizeObserver fires an initial callback for every newly observed element, so evaluation
  // happens asynchronously, outside the change-detection pass (avoids NG0100).
  private observeRateEquationAreas(): void {
    const observer = this.rateEquationResizeObserver;
    if (!observer) {
      return;
    }
    const current = new Set<HTMLElement>([
      ...this.rateEquationAreas.map((ref) => ref.nativeElement),
      ...this.rowContents.map((ref) => ref.nativeElement),
    ]);
    this.observedRateEquationEls.forEach((el) => {
      if (!current.has(el)) {
        observer.unobserve(el);
      }
    });
    current.forEach((el) => {
      if (!this.observedRateEquationEls.has(el)) {
        observer.observe(el);
      }
    });
    this.observedRateEquationEls = current;
  }

  // Coalesces bursts of observer callbacks (one per row on first observe, many during a window
  // drag) into a single measurement pass per frame, instead of one full-list reflow per callback.
  private scheduleRateEquationEvaluation(): void {
    if (this.destroyed || this.evaluationFrame !== undefined) {
      return;
    }
    this.evaluationFrame = requestAnimationFrame(() => {
      this.evaluationFrame = undefined;
      this.evaluateRateEquationOverflow();
    });
  }

  // Measuring the element as currently rendered would create a feedback loop once it's compact
  // (grid): entering compact mode changes the very box being measured. Swapping it to the flex
  // classes, reading scrollWidth/clientWidth, then swapping back - synchronously, before paint -
  // gives a stable "does the content fit" reading regardless of which mode it's currently in.
  private evaluateRateEquationOverflow(): void {
    if (this.destroyed) {
      return;
    }
    // Batched: put every element in single-line mode, read all, then restore each element's
    // exact original className (so [ngClass]'s own bookkeeping is never disturbed) - one reflow
    // instead of one per row.
    const entries = this.rateEquationAreas
      .map((ref) => ref.nativeElement)
      .filter((el) => !!el.dataset["symbolId"])
      .map((el) => ({ el, original: el.className }));
    // Decisions must not depend on the current mode (each mode changes the code column's width,
    // which would otherwise flip the decision straight back -> flicker). Each tier compares the
    // area's natural (max-content) width in that tier's layout with the row's width minus the
    // code column's fixed minimum for that tier.
    const measure = (classes: string, codeMin: number): boolean => {
      entries.forEach(({ el }) => (el.className = classes));
      return entries.some(({ el }) => {
        const parent = el.parentElement;
        if (!parent) {
          return false;
        }
        const gap = parseFloat(getComputedStyle(parent).columnGap) || 0;
        return el.getBoundingClientRect().width > parent.clientWidth - codeMin - gap + 1;
      });
    };
    const needsCompact = measure(
      "tw-flex tw-flex-none tw-items-center tw-whitespace-nowrap tw-gap-2",
      RATE_EQUATION_CODE_MIN_WIDTH,
    );
    entries.forEach(({ el, original }) => (el.className = original));
    // All-or-nothing: mixed layouts within one list look inconsistent, so if any row needs a
    // tier, every row gets it.
    const next = new Set<string>(
      needsCompact ? entries.map(({ el }) => el.dataset["symbolId"] as string) : [],
    );
    const current = this.compactRateEquationIds();
    if (next.size === current.size && [...next].every((id) => current.has(id))) {
      return;
    }
    // Evaluation already runs in its own animation frame (see scheduleRateEquationEvaluation), so
    // the layout switch never resizes an observed element mid-ResizeObserver-delivery. That
    // frame may be outside Angular's zone, hence ngZone.run.
    this.ngZone.run(() => this.compactRateEquationIds.set(next));
  }

  newSelectedSymbolCode = "";
  symbolCollection = computed(() => this.transactionStore.symbols.collection());
  selectedSymbolComputed = computed(() => {
    const newSelectedSymbol = this.symbolCollection()?.find(
      (s) => s.code === this.newSelectedSymbolCode,
    );
    return newSelectedSymbol;
  });

  private injector = inject(Injector);

  constructor() {
    this.dialogFactory = new DialogFactory();

    // call setSelectedSymbol when selectedSymbolComputed changes
    effect(() => {
      const updatedSymbol = this.selectedSymbolComputed();
      if (updatedSymbol) {
        this.setSelectedSymbol(updatedSymbol);
      }
    });

    // groupTotals() resolves asynchronously (after the transaction store loads), often later
    // than ngAfterViewInit — recompute the scroll arrows once the tiles have actually rendered.
    effect(() => {
      this.groupTotals();
      afterNextRender(() => this.updateGroupStatsScrollArrows(), { injector: this.injector });
    });

    // SymbolTableSubscription.source now returns the real null-loading sentinel (see its
    // guard against SymbolsStoreCollection.merge() overwriting the [] seed with null), so
    // this.table.data does distinguish "store hasn't populated yet" from "genuinely no such
    // symbol". This still doesn't clear symbolCode on a miss, though — clearDeepLinkQueryParam
    // replaces history, so a wrong clear can't be undone; only selects a match when one shows up.
    deepLinkParamReady$(this.symbolCode$, toObservable(this.table.data))
      .pipe(takeUntilDestroyed())
      .subscribe((symbolCode) => {
        const matchingSymbol = this.table.flatData().find((item) => item.code === symbolCode);
        if (
          matchingSymbol?.rawSymbol &&
          this.table.selectedSymbol()?.code !== matchingSymbol.rawSymbol.code
        ) {
          this.table.onRowClicked(matchingSymbol.rawSymbol);
        }
      });
  }

  handleAddSymbolClick() {
    this.dialogFactory.addSymbol();
  }

  closeSymbolDetails() {
    this.table.selectedSymbol.set(null);
  }

  setSelectedSymbol(selectedSymbol: ISymbolView) {
    this.table.selectSymbolRow(selectedSymbol);
  }

  onSymbolUpdated(newCode: string) {
    this.newSelectedSymbolCode = newCode;
  }

  // The rate-equation area (1.00 CODE / @1:rate chip / rate QUOTE) makes sense for
  // tradeable securities, FX currency pairs, and "other" priced assets. GLOSS Credits
  // (CrS/CrP) default into the "securities" group (they're not ISO currencies) but are
  // bookkeeping symbols, not priced assets, so they're excluded even though their group
  // would otherwise qualify. Symbols whose latest reference data is mock/test-sourced (no
  // real quote behind them) are excluded too.
  protected showRateEquation(item: SymbolCardView, group: SymbolGroup): boolean {
    const source = item.rawSymbol?.c_asOf?.source ?? "";
    return (
      !!item.rawSymbol?.c_asOf?.symbol &&
      (group === "securities" || group === "cash" || group === "other") &&
      !isGlossCreditsSymbol(item.code) &&
      source.toLowerCase() !== ReferenceDataSource.MOCK
    );
  }

  // Boss rule: figures >= 1,000,000 switch to compact M/B notation (via the macroNumber pipe);
  // below that, the list keeps full thousands-separated numbers, never "k". Applies to every
  // rate shown in the rate-equation area (stocks, currencies, and "other" priced assets alike) -
  // showRateEquation() already gates which rows get here at all.
  protected isCompactValue(value: number | null | undefined): boolean {
    return Math.abs(value ?? 0) >= MacroNumbers.MILLION;
  }
}
